import React, { useEffect, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { useVerifyEmailMutation } from '../api/auth';
import Logo from '../components/Logo';
import { useTypedSelector } from '../store';
import { saveSession } from '../utils/session';

const VerifyUserPage: React.FC = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [status, setStatus] = useState<'pending' | 'success' | 'error'>(
		'pending',
	);
	const [message, setMessage] = useState<string>('Verifying your email...');

	const didRunRef = useRef(false);

	// Get tenant from Redux state (set by TenantInitializer)
	const tenant = useTypedSelector((state) => state.tenant.tenant);
	const tenantLoading = useTypedSelector((state) => state.tenant.loading);

	// RTK Query mutation for email verification
	const [verifyEmail] = useVerifyEmailMutation();

	// Determine which logo to show
	// Show RocketFee logo only for app.rocketfee.com or rocketfee-dev.mightybot.ai
	// For all other domains, wait for tenant data to load, then show tenant logo if available
	const hostname = window.location.hostname;
	const isRocketFeeDomain =
		hostname === 'app.rocketfee.com' ||
		hostname === 'dev.rocketfee.com' ||
		hostname === 'rocketfee-dev.mightybot.ai';

	// Use tenant logo if available and not on RocketFee domain
	// For non-RocketFee domains, wait for tenant to load before showing logo
	const logoToShow = isRocketFeeDomain
		? null // Always show RocketFee logo for RocketFee domains
		: tenant
			? tenant.logo_base64 || null
			: undefined; // undefined = wait for tenant to load

	useEffect(() => {
		if (didRunRef.current) return;

		const params = new URLSearchParams(location.search);
		const token = params.get('token');
		const pending = params.get('pending');
		const email = params.get('email')
			? decodeURIComponent(params.get('email')!)
			: null;

		if (!token) {
			didRunRef.current = true;
			if (pending) {
				setStatus('pending');
				setMessage(
					email
						? `We have sent a verification link to ${email}. Please check your inbox and click the link to activate your account.`
						: 'We have sent a verification link to your email. Please check your inbox and click the link to activate your account.',
				);
				return;
			}
			setStatus('error');
			setMessage('Missing verification token');
			return;
		}

		// Wait for tenant to load before verifying (tenant context is required for multi-tenancy)
		// Only proceed if tenant is loaded (not loading AND tenant exists)
		if (tenantLoading || !tenant) {
			console.log('[VerifyEmail] Waiting for tenant to load...', {
				tenantLoading,
				hasTenant: !!tenant,
			});
			return; // Exit and wait for next render when tenant is loaded
		}

		// Mark as run only after tenant is loaded and we're about to verify
		didRunRef.current = true;

		const verify = async () => {
			try {
				console.log('[VerifyEmail] Starting verification with tenant context', {
					tenantId: tenant.id,
					token: token.substring(0, 8) + '...',
				});
				// Use RTK Query mutation - it will automatically add X-Tenant-Id header
				const result = await verifyEmail(token).unwrap();

				if ('token' in result && result.token) {
					saveSession(result);
					setTimeout(() => navigate('/'), 300);
				}
				setStatus('success');
				setMessage('Your email has been verified successfully.');
			} catch (error: any) {
				setStatus('error');
				setMessage(
					error?.data?.message || 'Verification link is invalid or expired.',
				);
			}
		};
		verify();
	}, [location.search, tenantLoading, tenant, navigate, verifyEmail]);

	const getStatusIcon = () => {
		switch (status) {
			case 'success':
				return (
					<div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
						<svg
							className="h-6 w-6 text-green-600"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M5 13l4 4L19 7"
							/>
						</svg>
					</div>
				);
			case 'error':
				return (
					<div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
						<svg
							className="h-6 w-6 text-red-600"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</div>
				);
			default:
				return (
					<div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
						<svg
							className="h-6 w-6 text-blue-600"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
							/>
						</svg>
					</div>
				);
		}
	};

	return (
		<div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
			<div className="sm:mx-auto sm:w-full sm:max-w-md">
				<div className="flex justify-center mb-6">
					{logoToShow !== undefined ? (
						<Logo logoBase64={logoToShow} alt={tenant?.name || 'RocketFee'} />
					) : (
						// Show placeholder while tenant is loading (same size as logo to prevent layout shift)
						<div className="h-6 w-auto" />
					)}
				</div>
				<div className="text-center">
					<h1 className="text-3xl font-bold text-gray-900 mb-2">
						Email Verification
					</h1>
				</div>
			</div>

			<div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
				<div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
					{getStatusIcon()}

					<p
						className={`text-lg mb-6 ${
							status === 'error'
								? 'text-red-600'
								: status === 'success'
									? 'text-green-600'
									: 'text-gray-600'
						}`}
					>
						{message}
					</p>

					<div className="mt-6">
						<button
							onClick={() => navigate('/auth?type=login')}
							className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
						>
							Go to Sign In
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default VerifyUserPage;
